import { useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/DataTable'
import { Modal } from '@/components/Modal'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { ApiError } from '@/lib/api'
import { formatDateTime } from '@/lib/locale'
import {
  useActivateStaffMember,
  useChangeStaffRole,
  useDeactivateStaffMember,
  useDeleteStaffMember,
  useStaffMembers,
  useStaffRoles,
} from '@/features/platformAdmin/queries'
import type { StaffMember } from '@/features/platformAdmin/types'

const fmtDate = (v: string | null) => (v ? formatDateTime(v) : '-')

const errMsg = (err: unknown, fallback = 'Action failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function StaffTab() {
  const { hasPermission, user } = useAuth()
  const { toast } = useToast()
  const membersQ = useStaffMembers()
  const rolesQ = useStaffRoles()
  const changeRole = useChangeStaffRole()
  const deactivate = useDeactivateStaffMember()
  const activate = useActivateStaffMember()
  const deleteStaff = useDeleteStaffMember()
  const canManage = hasPermission('admin.staff.manage')
  const canDelete = hasPermission('admin.staff.delete')

  const [confirm, setConfirm] = useState<{
    title: string
    body: string
    onConfirm: () => Promise<void>
    confirmLabel?: string
  } | null>(null)

  if (membersQ.isLoading || rolesQ.isLoading) {
    return (
      <Card>
        <CardContent className="py-10">
          <LoadingSpinner />
        </CardContent>
      </Card>
    )
  }

  const members = membersQ.data?.members ?? []
  const roles = rolesQ.data?.roles ?? []

  const currentUserId = user?.userId ? Number(user.userId) : null

  const requestDeactivate = (m: StaffMember) =>
    setConfirm({
      title: 'Deactivate staff',
      body: `Revoke ${m.email}'s access?`,
      confirmLabel: 'Deactivate',
      onConfirm: async () => {
        try {
          await deactivate.mutateAsync(m.user_id)
          toast({ title: 'Staff deactivated.' })
        } catch (err) {
          toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
        }
      },
    })

  const requestDelete = (m: StaffMember) =>
    setConfirm({
      title: 'Delete staff member',
      body: `Permanently delete ${m.email}? This removes their account, all login sessions, and every record they own. This action cannot be undone.`,
      confirmLabel: 'Delete permanently',
      onConfirm: async () => {
        try {
          await deleteStaff.mutateAsync(m.user_id)
          toast({ title: 'Staff member deleted.' })
        } catch (err) {
          toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
        }
      },
    })

  const doActivate = async (m: StaffMember) => {
    try {
      await activate.mutateAsync(m.user_id)
      toast({ title: 'Staff activated.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
    }
  }

  const onRoleChange = async (m: StaffMember, roleId: number) => {
    try {
      await changeRole.mutateAsync({ userId: m.user_id, roleId })
      toast({ title: 'Role updated.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
    }
  }

  const columns: ColumnDef<StaffMember>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <span className="font-semibold">
          {row.original.name ?? '-'}
          {row.original.is_owner ? (
            <Badge variant="secondary" className="ml-2">
              owner
            </Badge>
          ) : null}
        </span>
      ),
    },
    { accessorKey: 'email', header: 'Email' },
    {
      id: 'role',
      header: 'Role',
      cell: ({ row }) => (
        <Select
          value={String(row.original.role_id)}
          onValueChange={(v) => onRoleChange(row.original, Number(v))}
          disabled={row.original.is_owner || !canManage}
        >
          <SelectTrigger className="h-8 w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {roles.map((r) => (
              <SelectItem key={r.id} value={String(r.id)}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ row }) =>
        row.original.is_active ? (
          <Badge variant="success">active</Badge>
        ) : (
          <Badge variant="danger">inactive</Badge>
        ),
    },
    {
      accessorKey: 'last_login_at',
      header: 'Last login',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">{fmtDate(row.original.last_login_at)}</span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const m = row.original
        if (m.is_owner) return <span className="text-xs text-muted-foreground">-</span>
        if (!canManage && !canDelete) return <span className="text-xs text-muted-foreground">-</span>
        const isSelf = currentUserId != null && currentUserId === m.user_id
        return (
          <div className="flex flex-wrap items-center gap-1">
            {canManage ? (
              m.is_active ? (
                <Button size="sm" variant="ghost" onClick={() => requestDeactivate(m)}>
                  Deactivate
                </Button>
              ) : (
                <Button size="sm" onClick={() => doActivate(m)}>
                  Activate
                </Button>
              )
            ) : null}
            {canDelete && !isSelf ? (
              <Button
                size="sm"
                variant="danger"
                onClick={() => requestDelete(m)}
                disabled={deleteStaff.isPending}
              >
                Delete
              </Button>
            ) : null}
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-0">
          <DataTable columns={columns} rows={members} empty="No staff members yet." />
        </CardContent>
      </Card>

      <Modal.Confirm
        open={!!confirm}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm?.title ?? ''}
        body={confirm?.body ?? ''}
        confirmLabel={confirm?.confirmLabel ?? 'Confirm'}
        onConfirm={async () => {
          const c = confirm
          setConfirm(null)
          if (c) await c.onConfirm()
        }}
      />
    </div>
  )
}
